"use client";
import { Fragment, useState, useMemo } from "react";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { REGIONS } from "@/data/regions-data";

function classNames(...classes: (string | boolean | undefined)[]): string {
  return classes.filter(Boolean).join(" ");
}

type Region = {
  id: string;
  name: string;
  emoji: string;
  type: "region";
};

interface RegionSelectorProps {
  handleMenuItemClick: (item: Region) => void;
  value: Region | null;
  loading?: boolean;
}

export default function RegionSelector({
  handleMenuItemClick,
  value,
  loading = false,
}: RegionSelectorProps) {
  const [searchQuery, setSearchQuery] = useState("");

  // Filter regions based on search query
  const filteredRegions = useMemo(() => {
    if (!searchQuery.trim()) {
      return REGIONS;
    }

    const query = searchQuery.toLowerCase().trim();
    return REGIONS.filter((region) => {
      const regionName = region.name?.toLowerCase() || "";
      const regionId = region.id?.toLowerCase() || "";
      return regionName.includes(query) || regionId.includes(query);
    });
  }, [searchQuery]);

  return (
    <Menu as="div" className="w-full relative inline-block text-left">
      <div>
        <Menu.Button className="inline-flex w-full justify-between items-center gap-x-1.5 rounded-xl bg-white px-4 py-3 text-base font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-all">
          <span className="flex items-center gap-2">
            {loading ? (
              <>
                <ArrowPathIcon className="h-5 w-5 text-[#090EDB] animate-spin" />
                <span className="text-gray-500">Loading...</span>
              </>
            ) : value ? (
              <>
                <span>{value.emoji}</span>
                <span>{value.name}</span>
              </>
            ) : (
              <span className="text-gray-500">Select Region</span>
            )}
          </span>
          {!loading && (
            <ChevronDownIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          )}
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="max-h-[50vh] h-auto overflow-y-scroll p-2 absolute left-0 z-50 mt-2 w-full origin-top-right rounded-2xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none will-change-transform">
          <div className="py-1">
            <div className="px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider bg-gradient-to-r from-primary/10 via-primary/5 to-[#E73DC4]/10 rounded-xl mb-3 border border-primary/20 shadow-sm">
              <div className="flex items-center gap-2">
                <span className="text-base">🌍</span>
                <span>Regions</span>
              </div>
            </div>
            
            {/* Search input */}
            <div className="px-2 mb-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search region..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#090EDB] focus:border-transparent"
                />
              </div>
            </div>

            {/* Regions list */}
            {filteredRegions.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {filteredRegions.map((region) => (
                  <Menu.Item key={`region-${region.id}`}>
                    {({ active }) => (
                      <button
                        onClick={() => {
                          handleMenuItemClick({ ...region, type: "region" });
                          setSearchQuery(""); // Reset search after selection
                        }}
                        className={classNames(
                          active 
                            ? "bg-gradient-to-br from-primary/20 via-primary/10 to-[#E73DC4]/20 border-primary/50 shadow-md scale-[1.02]" 
                            : "bg-gradient-to-br from-white to-gray-50 hover:from-primary/5 hover:to-[#E73DC4]/5",
                          "text-gray-900 block px-4 py-3 w-full text-left rounded-xl border-2 border-gray-200 hover:border-primary/40 hover:shadow-lg transition-all duration-300 group"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl group-hover:scale-110 transition-transform duration-300">
                            {region.emoji}
                          </span>
                          <span className="font-bold text-sm group-hover:text-primary transition-colors duration-300">
                            {region.name}
                          </span>
                        </div>
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </div>
            ) : (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                No regions found matching &quot;{searchQuery}&quot;
              </div>
            )}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
